import { ToggleAudioPlayer } from '@doltech/ui/lib/figma/Audio/Player/ToggleAudioPlayer';
import { BlogCoverEditor } from '@doltech/ui/lib/figma/Blog/BlogCoverEditor';
import { withStoreRequiredForEditor } from '@doltech/ui/lib/figma/BlogEditor/core/withStoreRequiredForEditor';
import { BreadcrumbDolVn } from '@doltech/ui/lib/figma/Breadcrumb/BreadcrumbDolVn';
import { Box } from '@doltech/ui/lib/figma/Common/BoxV2';
import { NewsExtraInfo } from '@doltech/ui/lib/figma/DailyLearning/UserView/NewsExtraInfo';
import { useFileUploadStore } from '@doltech/ui/lib/figma/DolEditor/elements/BlogEditor/model/file-upload-model';
import { Separator } from '@doltech/ui/lib/figma/Layout/Landing/Content/Separator';
import { deviceBreakpoints } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { compose } from '@doltech/utils/lib/compose';
import { NewsContentTypeEnum } from '@doltech/utils/lib/constants';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { DynamicPageProps } from '../../../../../../@types/DynamicRouting';
import { EditorWithPlaceHolder } from '../../admin-blog-management/component/template/TitleTemplateComponent';
import { getInitialHeader } from '../data-entry/News/daily-news.utils';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';

const Main = styled.div`
  padding: 0 24px;
  ${fromScreen(541)} {
    margin: 0 auto;
  }
  .dol-editor {
    h1 {
      font-size: 32px;
      line-height: 40px;
      font-weight: 600;
      @media ${deviceBreakpoints.fromTablet} {
        font-size: 40px;
        line-height: 48px;
      }
    }
  }
`;

const EditorPlaceholderWrapper = styled.div`
  .title-template-placeholder {
    font-size: 29px;
    line-height: 36px;
  }
`;

interface DailyNewsHeaderProps extends DynamicPageProps {
  detailData: any;
}

const DailyNewsHeaderBase = ({ detailData, seoData, internalPageLink }: DailyNewsHeaderProps) => {
  const { thumbnail } = detailData;
  const fileUploadStore = useFileUploadStore();
  const headerObj = getInitialHeader(detailData);
  const headerKey = headerObj?.id;
  const renderHeaderContent = React.useCallback(() => {
    const extraInfoProps = {
      level: detailData.level,
      createdAt: detailData.createdAt,
      topic: detailData.topic,
      seoData,
    };
    switch (detailData.contentType) {
      case NewsContentTypeEnum.NEWS: {
        const element = headerObj?.content?.[1];
        let mediaUrl = element?.audioUrl || fileUploadStore.getFileById(element?.id)?.url;
        if (element?.audioType === 'GOOGLE') {
          mediaUrl = element?.audioTextUrl;
        }
        return (
          <>
            <NewsExtraInfo {...extraInfoProps} />
            {mediaUrl && (
              <Box mt="24px">
                <ToggleAudioPlayer titlePreview="Listen to this news" url={mediaUrl} />
              </Box>
            )}
            <Box mt="24px">
              <BlogCoverEditor
                width={thumbnail?.width}
                height={thumbnail?.height}
                src={thumbnail?.url}
                alt={thumbnail?.alt}
              />
            </Box>
          </>
        );
      }
      case NewsContentTypeEnum.VIDEO: {
        const videoKey = headerObj?.content?.[1]?.url || headerKey;
        return (
          <>
            <NewsExtraInfo {...extraInfoProps} />
            <Box mt="24px">
              <EditorPlaceholderWrapper>
                <EditorWithPlaceHolder
                  key={videoKey}
                  isReadOnly
                  initialValue={[headerObj?.content?.[1]]}
                />
              </EditorPlaceholderWrapper>
            </Box>
          </>
        );
      }
      case NewsContentTypeEnum.KNOWLEDGE: {
        return (
          <>
            <NewsExtraInfo {...extraInfoProps} />
            <Box mt="24px">
              <BlogCoverEditor
                width={thumbnail?.width}
                height={thumbnail?.height}
                src={thumbnail?.url}
                alt={thumbnail?.alt}
              />
            </Box>
          </>
        );
      }
      default:
    }
    return <NewsExtraInfo {...extraInfoProps} createdAt={detailData.createdAt} seoData={seoData} />;
  }, [
    detailData.contentType,
    detailData.createdAt,
    detailData.level,
    detailData.topic,
    fileUploadStore,
    headerKey,
    headerObj?.content,
    seoData,
    thumbnail?.alt,
    thumbnail?.height,
    thumbnail?.url,
    thumbnail?.width,
  ]);

  return (
    <Main className="daily-news-header title-template-wrapper">
      <Box mb="16px">
        <BreadcrumbDolVn
          routes={[
            {
              path: internalPageLink?.staticPageLink?.LANDING_HOME?.url || '/',
              breadcrumbName: 'Trang chủ',
            },
            {
              path: internalPageLink?.staticPageLink?.LANDING_DAILY_LEARNING_NEWS?.url || '/',
              breadcrumbName: 'Tin tức tiếng anh',
            },
          ]}
        />
      </Box>
      <Box mb="8px">
        <EditorPlaceholderWrapper>
          <EditorWithPlaceHolder
            key={headerKey}
            isReadOnly
            initialValue={[headerObj?.content?.[0]]}
          />
        </EditorPlaceholderWrapper>
      </Box>

      {renderHeaderContent()}

      <Separator mt="24px" variant="three-dot-horizontal" />
    </Main>
  );
};

export const DailyNewsHeader = compose(
  withStoreRequiredForEditor({
    initFileUploadImmediately: true,
  }),
  observer
)(DailyNewsHeaderBase);
